/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { modifier } from 'ember-modifier';
import type IntlTelInput from 'intl-tel-input';
import type { Iti, Iso2 } from 'intl-tel-input';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { type BsFormSignature } from 'ember-bootstrap/components/bs-form';

interface IArgs {
  form: BsFormSignature<ModelRegistry['booking']>;
  changeset: GenericChangeset<ModelRegistry['booking']>;
  onValidityChange?(isValid: boolean): void;
}

interface BookingEventGuestInfoSignature {
  Element: HTMLDivElement;

  Args: IArgs;

  Blocks: {
    default?: [boolean];
  };
}

const itiOptions = {
  initialCountry: 'us' as Iso2,
  countryOrder: ['gb', 'us', 'ca', 'au'] as Iso2[],
  separateDialCode: false,
  formatOnDisplay: false,
  nationalMode: false,
  loadUtils: () => import('intl-tel-input/utils'),
};

export default class UiBookingEventGuestInfoComponent extends Component<BookingEventGuestInfoSignature> {
  input!: HTMLInputElement;
  intlTelInput!: typeof IntlTelInput;
  phoneInput!: Iti;

  @tracked isValid = true;
  @tracked utilsLoaded = false;

  @cached
  get changeset(): GenericChangeset<ModelRegistry['booking']> {
    return this.args.changeset;
  }

  @cached
  get guestPhoneChanged(): boolean {
    return (
      !this.changeset.isNew &&
      this.changeset.changes.some((x) => x['key'] === 'guestPhone')
    );
  }

  handleInsert = modifier((input: HTMLInputElement) => {
    this.input = input;

    this.#handleInit();
  });

  async #handleInit(): Promise<void> {
    const intlTelInput = await import('intl-tel-input');

    if (this.isDestroying || this.isDestroyed) {
      return;
    }

    this.intlTelInput = intlTelInput.default;

    this.phoneInput = this.intlTelInput(this.input, itiOptions);

    await this.phoneInput.promise;

    if (this.isDestroying || this.isDestroyed) {
      return;
    }

    this.utilsLoaded = true;

    // validated only: rewriting the saved number would count as an edit
    if (this.input.value && this.input.value.trim()) {
      this.#validate();
    }
  }

  @action
  handleValidateAndUpdate(): void {
    if (!this.phoneInput || !this.utilsLoaded) {
      return;
    }

    this.#validate();

    if (this.isValid) {
      this.args.changeset.guestPhone = this.phoneInput.getNumber();
    }
  }

  #validate(): void {
    const { value } = this.input;

    this.isValid =
      value && value.trim() ? (this.phoneInput.isValidNumber() ?? false) : true;

    this.args.onValidityChange?.(this.isValid);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Event::GuestInfo': typeof UiBookingEventGuestInfoComponent;
  }
}
